import React from "react";
import { translateService, dateHelperService } from "../../services/imports";
import { LoadingButton } from "../../components/formcontrols/imports";
import { CloudSignerNotice } from "../../components/signers/cloudNotice";
import { isCloudSigner } from "../../services/signerManagementService";
import EvidenceFields, { keepsEvidence, evidenceBody, evidenceValid, keptLabels } from "./evidenceFields";

const t = (k, ...a) => translateService.t(`signers.${k}`, ...a);
const date = (ms) => (ms ? dateHelperService.format(ms, dateHelperService.smallDateTimeFormat) : "");
const DAY_MS = 24 * 60 * 60 * 1000;
// a certificate's request may be deleted with its signing tool only before
// anything was installed
const DELETABLE = ["NONE", "REQUESTED", "FAILED"];

// One signing tool of the workspace (the gateway's ManagedSigner): its name,
// kind, authentication and engine as the catalogue labels them (never the
// API's names), whether it is ready, deactivated or awaiting set-up and what
// for, its verification credentials' state (never their values) and, for a
// cloud kind, its key's certificate. The owner's controls only for the owner.
//
// props: signer, catalogue, owner, busy (the running action), message
// ({text, error}), highlight, actions {rename(name), deactivate, activate,
// remove, validate, credentials, request, requestAgain, downloadRequest,
// downloadCertificate, upload}
export default class SignerCard extends React.Component {
  state = { renaming: false, name: "", confirm: null, evidenceDraft: null, evidenceError: null };

  ref = React.createRef();

  componentDidMount() {
    if (this.props.highlight && this.ref.current) this.ref.current.scrollIntoView({ block: "nearest" });
  }

  componentDidUpdate(prev) {
    if (this.props.highlight && !prev.highlight && this.ref.current) this.ref.current.scrollIntoView({ block: "nearest" });
  }

  // ---- the catalogue's labels ------------------------------------------------------

  catalogue = () => this.props.catalogue || {};

  authLabels = () =>
    (this.props.signer.authTypes || [])
      .map((a) => ((this.catalogue().authTypes || []).find((x) => x.authType === a) || {}).label)
      .filter(Boolean);

  engine = () => (this.catalogue().engines || []).find((e) => e.engine === this.props.signer.verificationEngine) || null;

  // ---- parts -----------------------------------------------------------------------

  renderStatus() {
    const s = this.props.signer;
    const chips = [];
    if (!s.active) chips.push(["deactivated", t("status.deactivated", date(s.deactivatedAt))]);
    else if (s.provisioning && s.provisioning.state === "READY") chips.push(["ready", t("status.ready")]);
    else {
      // what is missing, by its label; one this site does not know: "awaiting set-up"
      const missing = ((s.provisioning && s.provisioning.missing) || [])
        .map((m) => translateService.t(`signers.missing.${m}`))
        .filter(Boolean);
      chips.push(["pending", missing.length ? t("status.awaitingSetupWhat", missing.join(", ")) : t("status.awaitingSetup")]);
    }
    if (s.verificationEngine && !s.verificationEngineAvailable) chips.push(["warning", t("status.engineUnavailable")]);
    return (
      <span className="signer-card__chips">
        {chips.map(([kind, text]) => (
          <span key={kind} className={`signer-card__chip is-${kind}`}>
            {text}
          </span>
        ))}
      </span>
    );
  }

  renderFacts() {
    const s = this.props.signer;
    const engine = this.engine();
    const auth = this.authLabels();
    // keyed by fact, not by label: before the texts load every label is ""
    const facts = [
      ["kind", t("facts.kind"), s.kindLabel],
      auth.length && ["auth", t("facts.authTypes"), auth.join(", ")],
      engine && ["engine", t("facts.engine"), engine.label],
      ["created", t("facts.created"), date(s.createdAt)],
    ].filter(Boolean);
    return (
      <dl className="signer-card__facts">
        {facts.map(([key, label, value]) => (
          <React.Fragment key={key}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </React.Fragment>
        ))}
      </dl>
    );
  }

  button = (key, label, onClick, colour = "background-color-one") => (
    <LoadingButton
      key={key}
      type="button"
      className={`button ${colour} color-white`}
      loading={this.props.busy === key}
      disabled={!!this.props.busy}
      onClick={onClick}
    >
      {label}
    </LoadingButton>
  );

  // the engine's credentials, where the engine takes them
  renderCredentials() {
    const s = this.props.signer;
    const engine = this.engine();
    if (!engine || !engine.takesCredentials) return null;
    const { owner, actions } = this.props;
    const c = s.credentials;
    return (
      <section className="signer-card__section">
        <h4>{t("credentials.title")}</h4>
        <p>
          {!c
            ? t("credentials.none")
            : c.validatedAt
            ? t("credentials.state", date(c.updatedAt), date(c.validatedAt), c.generation)
            : t("credentials.stateUnchecked", date(c.updatedAt), c.generation)}
        </p>
        {owner && (
          <div className="buttons">
            {c
              ? [
                  this.button("validate", t("credentials.check"), actions.validate),
                  this.button("credentials", t("credentials.replace"), actions.credentials),
                ]
              : this.button("credentials", t("credentials.enter"), actions.credentials, "background-color-two")}
          </div>
        )}
        {owner && c && <p className="signer-card__hint">{t("credentials.replaceWarning")}</p>}
      </section>
    );
  }

  // A face-verified tool's verification evidence: what each signing keeps
  // (the provider's files), changed by the owner; a session PDF still on
  // makes every signing fail for now, said so
  renderEvidence() {
    const s = this.props.signer;
    if (!keepsEvidence(s.authTypes)) return null;
    const { owner, actions } = this.props;
    const v = s.verificationEvidence || {};
    const { evidenceDraft, evidenceError } = this.state;
    const kept = keptLabels(v);
    const save = () => {
      if (!evidenceValid(evidenceDraft)) return this.setState({ evidenceError: t("evidence.atLeastOne") });
      actions.evidence(evidenceBody(evidenceDraft)).then((ok) => ok && this.setState({ evidenceDraft: null }));
    };
    return (
      <section className="signer-card__section">
        <h4>{t("evidence.title")}</h4>
        {evidenceDraft ? (
          <React.Fragment>
            <EvidenceFields
              value={evidenceDraft}
              error={evidenceError}
              idPrefix={`signer-evidence-${s.uuid}`}
              onChange={(next) => this.setState({ evidenceDraft: next, evidenceError: null })}
            />
            <div className="buttons">
              {this.button("evidence", t("save"), save, "background-color-two")}
              <LoadingButton
                type="button"
                className="button background-color-one color-white"
                disabled={!!this.props.busy}
                onClick={() => this.setState({ evidenceDraft: null, evidenceError: null })}
              >
                {t("cancel")}
              </LoadingButton>
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <p>{kept.length ? t("evidence.kept", kept.join(", ")) : t("evidence.none")}</p>
            {v.pdf && <p className="signer-card__warning">{t("evidence.pdfOnWarning")}</p>}
            {owner && (
              <div className="buttons">
                {this.button("evidenceEdit", t("evidence.change"), () =>
                  this.setState({ evidenceDraft: { image: !!v.image, video: !!v.video, pdf: false }, evidenceError: null })
                )}
              </div>
            )}
          </React.Fragment>
        )}
      </section>
    );
  }

  // the CSR round trip of a request awaiting the organization's certificate
  csrButtons = () => {
    const { actions } = this.props;
    return [
      this.button("downloadRequest", t("certificate.downloadRequest"), actions.downloadRequest),
      this.button("upload", t("certificate.upload"), actions.upload, "background-color-two"),
    ];
  };

  // the key's fingerprint, as a face signing's consent and the signed
  // document name it (INSTALLED or WITHDRAWN; exactly as given)
  renderKeyFingerprint(c) {
    if (!c.keyFingerprint) return null;
    return (
      <React.Fragment>
        <dt>{t("certificate.keyFingerprint")}</dt>
        <dd className="signer-card__mono">{c.keyFingerprint}</dd>
      </React.Fragment>
    );
  }

  renderInstalled(c) {
    const now = Date.now();
    const expired = c.notAfter && c.notAfter <= now;
    const soon = !expired && c.notAfter && c.notAfter - now < 30 * DAY_MS;
    return (
      <React.Fragment>
        <dl className="signer-card__facts">
          {c.subject && (
            <React.Fragment>
              <dt>{t("certificate.subject")}</dt>
              <dd className="signer-card__mono">{c.subject}</dd>
            </React.Fragment>
          )}
          {c.serial && (
            <React.Fragment>
              <dt>{t("certificate.serial")}</dt>
              <dd className="signer-card__mono">{c.serial}</dd>
            </React.Fragment>
          )}
          {c.notBefore && c.notAfter && (
            <React.Fragment>
              <dt>{t("certificate.valid")}</dt>
              <dd>{t("certificate.validRange", date(c.notBefore), date(c.notAfter))}</dd>
            </React.Fragment>
          )}
          {this.renderKeyFingerprint(c)}
        </dl>
        {c.keyFingerprint && <p className="signer-card__hint">{t("certificate.keyFingerprintHelp")}</p>}
        {expired && <p className="signer-card__warning">{t("certificate.expired", date(c.notAfter))}</p>}
        {soon && <p className="signer-card__warning">{t("certificate.expiresSoon", date(c.notAfter))}</p>}
      </React.Fragment>
    );
  }

  // a cloud kind's certificate, by its status (labels from the API)
  renderCertificate() {
    const s = this.props.signer;
    const c = s.certificate;
    if (!isCloudSigner(s) || !c) return null;
    const { owner, actions, polling } = this.props;
    const awaiting = c.status === "REQUESTED" && !c.csrAvailable;
    const pending = c.pendingRequest;
    const expired = c.notAfter && c.notAfter <= Date.now();
    let body = null;
    let controls = [];
    switch (c.status) {
      case "NONE":
        controls = [this.button("request", t("certificate.request"), actions.request, "background-color-two")];
        break;
      case "REQUESTED":
        if (awaiting) body = polling && <img src="./assets/images/black-loading.svg" className="signer-card__spinner" alt="" />;
        else {
          body = <p className="signer-card__hint">{t("certificate.csrHelp")}</p>;
          controls = this.csrButtons();
        }
        break;
      case "INSTALLED":
        body = this.renderInstalled(c);
        controls = [this.button("downloadCertificate", t("certificate.download"), actions.downloadCertificate)];
        if (!pending) controls.push(this.button("requestAgain", t("certificate.requestNew"), actions.requestAgain));
        break;
      case "FAILED":
        body = <p className="signer-card__warning">{t("certificate.failed", c.failureReasonLabel || c.failureReason || "")}</p>;
        controls = [this.button("requestAgain", t("certificate.requestAgain"), actions.requestAgain, "background-color-two")];
        break;
      case "WITHDRAWN":
        body = (
          <React.Fragment>
            <p>{t("certificate.withdrawn", date(c.withdrawnAt))}</p>
            {c.keyFingerprint && <dl className="signer-card__facts">{this.renderKeyFingerprint(c)}</dl>}
            <p className="signer-card__hint">{t("certificate.withdrawnHelp")}</p>
          </React.Fragment>
        );
        if (expired && !pending) controls = [this.button("requestAgain", t("certificate.requestNew"), actions.requestAgain)];
        break;
      default:
        break;
    }
    return (
      <section className="signer-card__section">
        <h4>{t("certificate.title")}</h4>
        <p>
          <strong>{c.statusLabel}</strong>
          {c.certificationLabel && <span className="signer-card__muted"> · {c.certificationLabel}</span>}
        </p>
        {body}
        {pending && (
          <div className="signer-card__pending">
            <p>
              <strong>{t("certificate.pending")}</strong>: {pending.statusLabel}
            </p>
            <p className="signer-card__muted">
              {t("certificate.requestedAt", date(pending.requestedAt))}
              {pending.answeredAt ? ` · ${t("certificate.answeredAt", date(pending.answeredAt))}` : ""}
            </p>
            {!pending.answeredAt && polling && (
              <img src="./assets/images/black-loading.svg" className="signer-card__spinner" alt="" />
            )}
          </div>
        )}
        {owner && pending && pending.csrAvailable && <div className="buttons">{this.csrButtons()}</div>}
        {owner && controls.length > 0 && <div className="buttons">{controls}</div>}
        {owner && c.status === "INSTALLED" && !pending && <p className="signer-card__hint">{t("certificate.requestNewHint")}</p>}
      </section>
    );
  }

  renderActions() {
    const s = this.props.signer;
    const { actions } = this.props;
    const { renaming, name, confirm } = this.state;
    const deletable = isCloudSigner(s) && s.certificate && DELETABLE.includes(s.certificate.status);
    if (renaming)
      return (
        <div className="signer-card__rename">
          <input
            type="text"
            maxLength={255}
            value={name}
            aria-label={t("facts.name")}
            onChange={(e) => this.setState({ name: e.target.value })}
          />
          <LoadingButton
            type="button"
            className="button background-color-two color-white"
            loading={this.props.busy === "rename"}
            disabled={!name.trim()}
            onClick={() => actions.rename(name.trim()).then((ok) => ok && this.setState({ renaming: false }))}
          >
            {t("save")}
          </LoadingButton>
          <LoadingButton type="button" className="button background-color-one color-white" onClick={() => this.setState({ renaming: false })}>
            {t("cancel")}
          </LoadingButton>
          <small>{t("renameHint")}</small>
        </div>
      );
    if (confirm)
      return (
        <div className="signer-card__confirm" role="alert">
          <p>{t(confirm === "delete" ? "deleteConfirm" : "deactivateConfirm")}</p>
          <div className="buttons">
            {this.button(
              confirm,
              t(confirm === "delete" ? "deleteYes" : "deactivate"),
              () => {
                this.setState({ confirm: null });
                (confirm === "delete" ? actions.remove : actions.deactivate)();
              },
              "background-color-three"
            )}
            <LoadingButton type="button" className="button background-color-one color-white" onClick={() => this.setState({ confirm: null })}>
              {t("cancel")}
            </LoadingButton>
          </div>
        </div>
      );
    return (
      <div className="buttons signer-card__actions">
        <LoadingButton
          type="button"
          className="button background-color-one color-white"
          disabled={!!this.props.busy}
          onClick={() => this.setState({ renaming: true, name: s.name })}
        >
          {t("rename")}
        </LoadingButton>
        {s.active ? (
          <LoadingButton
            type="button"
            className="button background-color-three color-white"
            disabled={!!this.props.busy}
            onClick={() => this.setState({ confirm: "deactivate" })}
          >
            {t("deactivate")}
          </LoadingButton>
        ) : (
          this.button("activate", t("activate"), actions.activate, "background-color-two")
        )}
        {deletable && (
          <LoadingButton
            type="button"
            className="button background-color-three color-white"
            disabled={!!this.props.busy}
            onClick={() => this.setState({ confirm: "delete" })}
          >
            {t("delete")}
          </LoadingButton>
        )}
      </div>
    );
  }

  render() {
    const s = this.props.signer;
    const { owner, message, highlight } = this.props;
    return (
      <article ref={this.ref} className={`signer-card ${s.active ? "" : "is-off"} ${highlight ? "is-new" : ""}`}>
        <header className="signer-card__head">
          <h3 className="signer-card__name">{s.name}</h3>
          {this.renderStatus()}
        </header>
        {this.renderFacts()}
        {isCloudSigner(s) && <CloudSignerNotice signer={s} />}
        {this.renderCredentials()}
        {this.renderEvidence()}
        {this.renderCertificate()}
        {message && (
          <p className={message.error ? "signer-card__error" : "signer-card__success"} role={message.error ? "alert" : "status"}>
            {message.text}
          </p>
        )}
        {owner && this.renderActions()}
        {owner && <p className="signer-card__hint">{t("immutableHelp")}</p>}
      </article>
    );
  }
}
